import { styled } from '@mui/material';

import { useMemo } from 'react';

 import { Typography } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { RefactoringUILogo, UxcelLogo } from '@app-velocity/icons';

import { useAppraisalContext } from '../../../../context/useAppraisalContext';

export type PoweredByProps = {
  by: 'uxcel' | 'refactoringui';
};

const StyledPoweredBy = styled('div')<{variant?: 'dark' | 'light' }>(({ theme, variant }) => ({
  padding: theme.spacing(3),
  backgroundColor: variant === 'dark' ? 'var(--background-background-secondary-color)' : 'var(--background-background-color)',
  border: '1px solid var(--stroke-stroke-secondary-color)',
  borderRadius: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--background-background-secondary-color)',
  }
}));

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 500,
  color: 'var(--text-text-primary-color)',
}));

const Description = styled(Typography)({
  fontWeight: 300,
  fontSize: 13,
  lineHeight: 1.2,
  color: 'var(--text-text-secondary-color)',
});

const LogoContainer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    svg: {
        maxWidth: 70,
        maxHeight: 20,
        width: 'auto'
    }
})

export const PoweredBy = ({ by }: PoweredByProps) => {
  const { t } = useI18n();
  const { variant = 'light' } = useAppraisalContext();

  const data = useMemo(() => {
    switch (by) {
      case 'uxcel': {
        return {
          title: t('appraisals:powered_by.uxcel.title'),
          description: t('appraisals:powered_by.uxcel.description'),
          link: 'https://app.uxcel.com/courses/design-accessibility',
          logo: <UxcelLogo />,
        };
      }

      case 'refactoringui': {
        return {
          title: t('appraisals:powered_by.refactoringui.title'),
          description: t('appraisals:powered_by.refactoringui.description'),
          link: 'https://www.refactoringui.com/',
          logo: <RefactoringUILogo />
        };
      }

      default:
        return null;
    }
  }, [by, t]);

  const goto = (link: string) => {
    window.open(link);
  }

  if (!data) return null;

  const { title, description, logo, link } = data;

  return (
    <StyledPoweredBy variant={variant} onClick={() => goto(link)}>
      <div>
        <Title>{title}</Title>
        {description ? <Description>{description}</Description> : null}
      </div>
      {logo ? <LogoContainer>{logo}</LogoContainer> : null}
    </StyledPoweredBy>
  );
};
